import { modaleConfig } from "@/router/routes";
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Location, useLocation } from "react-router-dom";

interface ModaleLocationContextType {
    modaleLocation: Location | undefined;
    /**
     * True when the browser URL is the dashboard (modal slid away). Routes
     * rendered with `location={modaleLocation}` stay mounted at that point,
     * so they cannot detect the close through useLocation themselves.
     */
    isModaleClosed: boolean;
}

const ModaleLocationContext = createContext<
    ModaleLocationContextType | undefined
>(undefined);

interface ModaleLocationProviderProps {
    children: ReactNode;
}

export const ModaleLocationProvider = ({
    children,
}: ModaleLocationProviderProps) => {
    const location = useLocation();
    const [modaleLocation, setModaleLocation] = useState<Location | undefined>(
        undefined,
    );
    const isModaleClosed =
        location.pathname === modaleConfig.pathWhenModalClosed;
    const value = useMemo(
        () => ({ modaleLocation, isModaleClosed }),
        [modaleLocation, isModaleClosed],
    );

    useEffect(() => {
        const currentPath = location.pathname;
        const rootPath = currentPath.split("/")[1];

        const isMainPath = currentPath === modaleConfig.pathWhenModalClosed;
        const isModalPath = modaleConfig.modalPaths.some((modalPath) =>
            modalPath.includes(rootPath),
        );

        if (isMainPath && !modaleLocation) {
            setModaleLocation({
                ...location,
                pathname: modaleConfig.defaultPathWhenModalOpened,
            });
        } else if (isModalPath) {
            setModaleLocation({ ...location, pathname: currentPath });
        } else if (!isMainPath) {
            setModaleLocation(undefined);
        }
    }, [location]);

    return (
        <ModaleLocationContext.Provider value={value}>
            {children}
        </ModaleLocationContext.Provider>
    );
};

export const useModaleLocation = () => {
    const context = useContext(ModaleLocationContext);
    if (!context) {
        throw new Error(
            "useModaleLocation must be used within a ModaleLocationProvider",
        );
    }

    return context;
};
